import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import ReactDOM from 'react-dom';
import { Info, X } from 'lucide-react';

/**
 * CardInfo — reusable "i" info button + portal dialog for Command Center cards.
 *
 * Renders a small circular info button in a card header. Clicking it opens a
 * centered modal (portaled to document.body) describing what the card shows and
 * how its numbers are calculated.
 *
 * Usage:
 *   <div className="v3-cc-card-header-row">
 *       <h3>{t('My Card')}</h3>
 *       <CardInfo title={t('About My Card')}>
 *           <p>…explanation…</p>
 *       </CardInfo>
 *   </div>
 *
 * Extracted from the original inline implementation in PerformanceGaps.jsx so
 * every card can share one button + dialog markup and styling.
 */
export default function CardInfo({ title, children }) {
    const { t } = useTranslation();
    const [showInfo, setShowInfo] = useState(false);
    const toggleInfo = useCallback(() => setShowInfo(s => !s), []);

    return (
        <>
            <button
                className="v3-cc-info-btn"
                onClick={toggleInfo}
                aria-label={title || t('What is this card?')}
                title={t('What is this card?')}
            >
                i
            </button>

            {showInfo && ReactDOM.createPortal(
                <div className="v3-cc-info-overlay" onClick={toggleInfo}>
                    <div className="v3-cc-info-dialog" onClick={e => e.stopPropagation()}>
                        <div className="v3-cc-info-dialog-header">
                            <span className="v3-cc-info-dialog-title">
                                <span className="v3-cc-info-dialog-icon"><Info size={17} strokeWidth={2.5} /></span>
                                {title}
                            </span>
                            <button className="v3-cc-info-close" onClick={toggleInfo} aria-label={t('Close')}>
                                <X size={17} />
                            </button>
                        </div>
                        <div className="v3-cc-info-dialog-body">
                            {children}
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </>
    );
}
